import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabaseClient';

const { FiAlertTriangle, FiPhone, FiUser, FiActivity } = FiIcons;

export default function PublicEmergencyProfile() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [medicalInfo, setMedicalInfo] = useState([]);

  useEffect(() => {
    fetchProfileData();
  }, [token]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      // 1. Fetch Family Member by Token
      const { data: member, error: memberError } = await supabase
        .from('family_members')
        .select('*')
        .eq('share_token', token)
        .single();
      
      if (memberError || !member) throw new Error('Profil nicht gefunden oder Link ungültig.');
      
      setProfile(member);

      // 2. Fetch Emergency Contacts for this family
      const { data: contactData } = await supabase
        .from('emergency_contacts')
        .select('*')
        .eq('family_id', member.family_id);
      
      setContacts(contactData || []);

      // 3. Fetch Medical Info for this person
      // Note: people is an array of uuids. We need to find rows where people contains member.id
      const { data: medicalData } = await supabase
        .from('medical_info')
        .select('*')
        .eq('family_id', member.family_id)
        .contains('people', [member.id]); // Supabase array contains check

      setMedicalInfo(medicalData || []);

    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getMedicalTypeLabel = (type) => {
    switch(type) {
        case 'medication': return 'Medikation';
        case 'allergy': return 'Allergie';
        case 'condition': return 'Erkrankung';
        default: return 'Sonstiges';
    }
  };

  const getMedicalTypeColor = (type) => {
    switch(type) {
        case 'medication': return 'text-blue-700 bg-blue-50 border-blue-200';
        case 'allergy': return 'text-red-700 bg-red-50 border-red-200';
        case 'condition': return 'text-purple-700 bg-purple-50 border-purple-200';
        default: return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md w-full">
        <SafeIcon icon={FiAlertTriangle} className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Fehler</h2>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        
        {/* Header Profile */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-red-600 h-24"></div>
            <div className="px-6 pb-6 relative">
                <div className="w-24 h-24 bg-white rounded-full border-4 border-white shadow-md absolute -top-12 flex items-center justify-center text-3xl font-bold text-gray-700">
                    {profile.avatar || profile.name.charAt(0)}
                </div>
                <div className="mt-14">
                    <h1 className="text-3xl font-bold text-gray-900">{profile.name}</h1>
                    <p className="text-gray-500 font-medium uppercase tracking-wide text-sm mt-1">Notfall-Profil</p>
                </div>
            </div>
        </div>

        {/* SOS Actions */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between text-center sm:text-left space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
                <div className="bg-red-100 p-3 rounded-full">
                    <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 text-red-600" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-red-800">Notruf wählen</h2>
                    <p className="text-red-700 text-sm">Im lebensbedrohlichen Notfall</p>
                </div>
            </div>
            <a href="tel:112" className="bg-red-600 text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-red-700 shadow-md w-full sm:w-auto">
                112 Anrufen
            </a>
        </div>

        {/* Medical Info */}
        {medicalInfo.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <h2 className="text-lg font-bold flex items-center text-gray-800">
                        <SafeIcon icon={FiActivity} className="w-5 h-5 mr-2 text-red-500" />
                        Medizinische Daten
                    </h2>
                </div>
                <div className="p-4 grid gap-4 grid-cols-1 md:grid-cols-2">
                    {medicalInfo.map(info => (
                        <div key={info.id} className={`p-4 rounded-lg border ${getMedicalTypeColor(info.type)}`}>
                            <div className="flex justify-between items-start mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider opacity-75 border border-current px-1.5 py-0.5 rounded">
                                    {getMedicalTypeLabel(info.type)}
                                </span>
                            </div>
                            <h3 className="text-lg font-bold mb-1">{info.title}</h3>
                            {(info.dosage || info.frequency) && (
                                <div className="text-sm opacity-90 mb-2">
                                    {info.dosage && <span className="mr-2 font-medium">{info.dosage}</span>}
                                    {info.frequency && <span>({info.frequency})</span>}
                                </div>
                            )}
                            {info.description && <p className="text-sm opacity-80 italic">{info.description}</p>}
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* Contacts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
             <div className="p-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold flex items-center text-gray-800">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 mr-2 text-green-600" />
                    Notfallkontakte
                </h2>
            </div>
            <div className="divide-y divide-gray-100">
                {contacts.map(contact => (
                    <div key={contact.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-gray-50">
                        <div className="mb-3 sm:mb-0">
                            <h3 className="font-bold text-gray-900">{contact.name}</h3>
                            <p className="text-sm text-gray-500">{contact.role}</p>
                            {contact.address && <p className="text-xs text-gray-400 mt-1">📍 {contact.address}</p>}
                        </div>
                        <a href={`tel:${contact.phone}`} className="flex items-center justify-center px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium">
                            <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" />
                            {contact.phone}
                        </a>
                    </div>
                ))}
            </div>
        </div>

        <div className="text-center text-gray-400 text-xs py-4">
            Familiensicherheits-Profil • Generiert am {new Date().toLocaleDateString()}
        </div>

      </div>
    </div>
  );
}